import { useMemo } from 'react';
import { FormApi } from 'final-form';

import { snackbar } from '@occc-app/design-system';
import { OfferFragment } from '@occc/graphql';
import copies from '@occc-app/routes/CampaignComponents/copies';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import { useIsUserPermittedTo } from '@occc/utils-access-control';

import { useMakeOffersFilter } from './use-make-offers-filter.hook';

import { Flow_Types, FormContainerProps, GetConfigureFormOperations } from '../types';
import { useExecuteOperations } from '../operations/useExecuteOperations';
import { mapFormTypeToPermission } from '../utils/offerPermission';

export const useGetConfigureFormProps = <FVT, IVT>(
  formContainerProps: FormContainerProps,
  transformOfferToInitialValues: (offer?: OfferFragment) => IVT,
  getConfigureFormOperations: GetConfigureFormOperations<FVT>,
  options?: { onSubmitSuccess?: (values: FVT) => void },
) => {
  const {
    offer,
    offerId,
    onError,
    offerLoading,
    onCancel,
    currentFlowType,
    onRefetchOffer,
    setTabValue,
  } = formContainerProps;
  const makeOffersFilter = useMakeOffersFilter();

  const initialValues = useMemo(
    () => transformOfferToInitialValues(offer || undefined),
    [offer, transformOfferToInitialValues],
  );

  const [executeOperations, loading] = useExecuteOperations<FVT, IVT>({
    offerId,
    offer,
    getOperations: getConfigureFormOperations,
  });

  const isFormEditable = useIsUserPermittedTo(mapFormTypeToPermission[formContainerProps.type]);

  const onCompleted = async () => {
    await onRefetchOffer();

    snackbar.success(copies.onSubmitSuccessToastMessage);

    if (currentFlowType === Flow_Types.Configure) {
      setTabValue(CampaignComponentTabs.Target);
    }
  };

  const onNextStepButtonClick = () => {
    setTabValue(CampaignComponentTabs.Target);
  };

  const onSubmit = async (values: FVT, form: FormApi<FVT, IVT>) => {
    try {
      await executeOperations(values, form.getState());
      await onCompleted();
      options?.onSubmitSuccess?.(values);
    } catch (error) {
      onError(error);
    }
  };

  return {
    initialValues,
    onSubmit,
    isFormEditable,
    makeOffersFilter,
    loading: offerLoading || loading,
    onCancelClick: onCancel,
    currentFlowType,
    onNextStepButtonClick,
  };
};
